import React, { useState, useEffect } from 'react';
import { View, Text, Modal, TouchableOpacity, StyleSheet, ScrollView, Platform, Pressable } from 'react-native';
import ReactDOM from 'react-dom';
import { BACKEND_URL } from '../../../Config/Const';
import { useNavigation } from '@react-navigation/native';

const sortByOrderThenName = (items = [], nameKey = 'nombre') => {
  const safeItems = Array.isArray(items) ? [...items] : [];
  return safeItems.sort((a = {}, b = {}) => {
    const orderA = typeof a.orden === 'number' ? a.orden : null;
    const orderB = typeof b.orden === 'number' ? b.orden : null;

    if (orderA !== null && orderB !== null && orderA !== orderB) {
      return orderA - orderB;
    }
    if (orderA !== null && orderB === null) {
      return -1;
    }
    if (orderA === null && orderB !== null) {
      return 1;
    }

    const nameA = (a[nameKey] || '').toString().toUpperCase();
    const nameB = (b[nameKey] || '').toString().toUpperCase();
    return nameA.localeCompare(nameB, 'es', { sensitivity: 'base' });
  });
};

const sortHierarchy = (departments = []) => {
  return sortByOrderThenName(departments, 'departamento').map((dept) => ({
    ...dept,
    categorias: sortByOrderThenName(dept?.categorias, 'categoria').map((cat) => ({
      ...cat,
      subCategorias: sortByOrderThenName(cat?.subCategorias, 'subcategoria'),
    })),
  }));
};

const CategoriasMobil = ({ visible, onClose, onCategorySelect }) => {
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigation = useNavigation();

  // Estados para navegaciÃ³n drill-down
  const [currentView, setCurrentView] = useState('departments'); // 'departments' | 'categories'
  const [activeDepartment, setActiveDepartment] = useState(null);
  const [expandedCategory, setExpandedCategory] = useState(null);

  // Fetch categories from API
  useEffect(() => {
    const fetchCategories = async () => {
      if (visible) {
        setLoading(true);
        setError(null);

        try {
          const response = await fetch(`${BACKEND_URL}/rest/productos/categorias`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            },
          });

          if (!response.ok) {
            throw new Error(`Error: ${response.status} - ${response.statusText}`);
          }

          const data = await response.json();

          if (data.dsResultado && data.dsResultado.departamentos) {
            const sortedDepartments = sortHierarchy(data.dsResultado.departamentos);
            setDepartments(sortedDepartments);
          } else {
            console.error('âŒ Formato de respuesta inesperado:', data);
            setError('Formato de respuesta inesperado');
          }
        } catch (err) {
          console.error('Error fetching categories:', err);
          setError('No se pudieron cargar las categorÃ­as');
        } finally {
          setLoading(false);
        }
      }
    };

    fetchCategories();
  }, [visible]);

  // Reset estados cuando se cierra el modal
  useEffect(() => {
    if (!visible) {
      setCurrentView('departments');
      setActiveDepartment(null);
      setExpandedCategory(null);
    }
  }, [visible]);

  // Tap en departamento -> mostrar categorÃ­as
  const handleDepartmentTap = (dept) => {
    setActiveDepartment(dept);
    setExpandedCategory(null);
    setCurrentView('categories');
  };

  // Volver a departamentos
  const handleBackToDepartments = () => {
    setCurrentView('departments');
    setActiveDepartment(null);
    setExpandedCategory(null);
  };

  // Tap en categorÃ­a -> expandir/ocultar subcategorÃ­as
  const handleCategoryTap = (category) => {
    const hasSubcategories = category.subCategorias && category.subCategorias.length > 0;
    const isExpanded = expandedCategory === category.codcat;
    
    if (hasSubcategories) {
      // Si tiene subcategorÃ­as, alternar entre expandir/colapsar
      setExpandedCategory(isExpanded ? null : category.codcat);
    } else {
      // Si no tiene subcategorÃ­as, navegar directamente
      navigateToCategory(category);
    }
  };

  // Navegar a categorÃ­a
  const navigateToCategory = (category) => {
    const deptIdForSearch =
      category.codDepto === 0 || category.codDepto
        ? category.codDepto
        : activeDepartment?.categorias?.[0]?.codDepto;
    const categoryId = category.codcat;
    const deptName = activeDepartment?.departamento || '';
    const catName = category.categoria || '';
    const ofe = !!category['oyc-rdw'];

    if (onCategorySelect) {
      onCategorySelect(deptIdForSearch, categoryId, 0, deptName, catName, '');
    }

    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      const searchParams = new URLSearchParams();
      searchParams.set('D', String(deptIdForSearch));
      searchParams.set('C', String(categoryId));
      searchParams.set('S', '0');
      searchParams.set('deptName', deptName);
      searchParams.set('catName', catName);
      searchParams.set('subcatName', '');
      searchParams.set('codBanner', '0');
      if (ofe) searchParams.set('ofe', 'true');

      const url = `/productos?${searchParams.toString()}`;
      const isOnProductsPage = window.location.pathname === '/productos';
      
      if (isOnProductsPage) {
        window.history.pushState({}, '', url);
        window.dispatchEvent(new Event('navigationChange'));
      } else {
        window.location.href = url;
      }
    } else if (navigation?.navigate) {
      navigation.navigate('Productos', {
        D: deptIdForSearch || 0,
        C: categoryId || 0,
        S: 0,
        deptName,
        catName,
        subcatName: '',
        ofe
      });
    }
    onClose();
  };

  // Navegar a subcategorÃ­a
  const navigateToSubcategory = (subcat, parentCategory) => {
    const deptId =
      parentCategory?.codDepto === 0 || parentCategory?.codDepto
        ? parentCategory?.codDepto
        : subcat.codDepto;
    const catId = parentCategory?.codcat || subcat.codcat;
    const subcatId = subcat.codsubcat;
    const deptName = activeDepartment?.departamento || '';
    const catName = parentCategory?.categoria || '';
    const subcatName = subcat.subcategoria || '';

    if (onCategorySelect) {
      onCategorySelect(deptId, catId, subcatId, deptName, catName, subcatName);
    }

    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      const searchParams = new URLSearchParams();
      searchParams.set('D', String(deptId));
      searchParams.set('C', String(catId));
      searchParams.set('S', String(subcatId));
      searchParams.set('deptName', deptName);
      searchParams.set('catName', catName);
      searchParams.set('subcatName', subcatName);
      searchParams.set('codBanner', '0');

      const url = `/productos?${searchParams.toString()}`;
      const isOnProductsPage = window.location.pathname === '/productos';
      
      if (isOnProductsPage) {
        window.history.pushState({}, '', url);
        window.dispatchEvent(new Event('navigationChange'));
      } else {
        window.location.href = url;
      }
    } else if (navigation?.navigate) {
      navigation.navigate('Productos', {
        D: deptId || 0,
        C: catId || 0,
        S: subcatId || 0,
        deptName,
        catName,
        subcatName
      });
    }
    onClose();
  };

  // Renderizar lista de departamentos
  const renderDepartments = () => (
    <ScrollView style={styles.listContainer}>
      {departments.map((dept) => (
        <TouchableOpacity
          key={dept.cod}
          style={styles.listItem}
          onPress={() => handleDepartmentTap(dept)}
        >
          <Text style={[
            styles.listItemText,            (dept?.tipo ?? 0) === 1 && styles.listItemTextBold,            (dept?.tipo ?? 0) >= 2 && styles.listItemTextBoldRed
          ]}>
            {dept.departamento}
          </Text>
          <Text style={styles.arrowRight}>›</Text>
        </TouchableOpacity>
      ))}
    </ScrollView>
  );

  // Renderizar categorÃ­as del departamento activo
  const renderCategories = () => {
    const categories = activeDepartment?.categorias || [];
    
    return (
      <ScrollView style={styles.listContainer}>
        {categories.map((category, index) => {
          const hasSubcategories = category.subCategorias && category.subCategorias.length > 0;
          const isExpanded = expandedCategory === category.codcat;
          
          return (
            <View key={`cat-${category.codcat}-${index}`}>
              <TouchableOpacity
                style={styles.listItem}
                onPress={() => handleCategoryTap(category)}
              >
                <Text style={[
                  styles.listItemText,(category?.tipo ?? 0) === 1 && styles.listItemTextBold,(category?.tipo ?? 0) >= 2 && styles.listItemTextBoldRed
                ]}>
                  {category.categoria}
                </Text>
                {hasSubcategories && (
                  <Text style={[styles.arrowRight, isExpanded && styles.arrowDown]}>
                    {isExpanded ? '›' : '›'}
                  </Text>
                )}
              </TouchableOpacity>
              
              {/* SubcategorÃ­as expandidas */}
              {isExpanded && hasSubcategories && (
                <View style={styles.subcategoriesContainer}>
                  {/* OpciÃ³n Ver todos */}
                  <TouchableOpacity
                    style={styles.subcategoryItem}
                    onPress={() => navigateToCategory(category)}
                  >
                    <Text style={styles.subcategoryTextVerTodos}>
                      Ver todos
                    </Text>
                  </TouchableOpacity>
                  {category.subCategorias.map((subcat, subIndex) => (
                    <TouchableOpacity
                      key={`subcat-${subcat.codsubcat}-${subIndex}`}
                      style={styles.subcategoryItem}
                      onPress={() => navigateToSubcategory(subcat, category)}
                    >
                      <Text style={styles.subcategoryText}>
                        {subcat.subcategoria}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>
              )}
            </View>
          );
        })}
      </ScrollView>
    );
  };

  // Contenido del modal
  const modalContent = (
    <Pressable style={styles.modalOverlay} onPress={onClose}>
      <Pressable style={styles.modalContent} onPress={(e) => e.stopPropagation()}>
        {/* Header dinÃ¡mico */}
        {currentView === 'departments' ? (
          <View style={styles.modalHeader}>
            <Text style={styles.modalTitle}>CATEGORIAS</Text>
            <TouchableOpacity onPress={onClose} style={styles.closeButton}>
              <Text style={styles.closeButtonText}>‹</Text>
            </TouchableOpacity>
          </View>
        ) : (
          <TouchableOpacity 
            style={styles.backHeader}
            onPress={handleBackToDepartments}
          >
            <Text style={styles.backArrow}>‹</Text>
            <Text style={styles.backHeaderText}>{activeDepartment?.departamento}</Text>
          </TouchableOpacity>
        )}

        {/* Content */}
        <View style={styles.modalBody}>
          {loading ? (
            <View style={styles.loadingContainer}>
              <Text style={styles.loadingText}>Cargando categorías...</Text>
            </View>
          ) : error ? (
            <View style={styles.errorContainer}>
              <Text style={styles.errorText}>{error}</Text>
            </View>
          ) : (
            currentView === 'departments' ? renderDepartments() : renderCategories()
          )}
        </View>
      </Pressable>
    </Pressable>
  );

  // Si no estÃ¡ visible, no renderizar nada
  if (!visible) return null;

  // En web, usar portal para asegurar que estÃ© por encima de todo
  if (Platform.OS === 'web' && typeof document !== 'undefined') {
    return ReactDOM.createPortal(modalContent, document.body);
  }

  // En mÃ³vil, usar Modal nativo
  return (
    <Modal
      visible={visible}
      transparent={true}
      animationType="fade"
      onRequestClose={onClose}
      statusBarTranslucent={true}
    >
      {modalContent}
    </Modal>
  );
};

const styles = StyleSheet.create({
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
    justifyContent: 'flex-start',
    ...(Platform.OS === 'web' ? {
      position: 'fixed',
      top: 90, // Debajo del header rojo
      left: 0,
      right: 0,
      bottom: 0,
      zIndex: 2147483647,
    } : {
      marginTop: 90,
    }),
  },
  modalContent: {
    backgroundColor: '#fff',
    width: '75%',
    maxHeight: '85%',
    ...(Platform.OS === 'web' ? {
      maxWidth: 280,
      boxShadow: '2px 0 10px rgba(0,0,0,0.15)',
    } : {
      shadowColor: '#000',
      shadowOffset: { width: 2, height: 0 },
      shadowOpacity: 0.15,
      shadowRadius: 10,
      elevation: 5,
    }),
  },
  // Header principal (vista departamentos)
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: '#e30613',
    paddingVertical: 12,
    paddingHorizontal: 15,
  },
  modalTitle: {
    color: '#fff',
    fontSize: 16,
    fontWeight: 'bold',
  },
  closeButton: {
    padding: 5,
  },
  closeButtonText: {
    color: '#fff',
    fontSize: 24,
    fontWeight: 'bold',
  },
  // Header con botÃ³n volver (vista categorÃ­as)
  backHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#fff',
    paddingVertical: 15,
    paddingHorizontal: 15,
    borderBottomWidth: 1,
    borderBottomColor: '#eee',
  },
  backArrow: {
    fontSize: 24,
    color: '#333',
    marginRight: 10,
    fontWeight: 'bold',
  },
  backHeaderText: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#333',
    textTransform: 'uppercase',
  },
  // Body
  modalBody: {
    flex: 1,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  loadingText: {
    fontSize: 14,
    color: '#666',
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  errorText: {
    fontSize: 14,
    color: '#e30613',
  },
  // Lista de items
  listContainer: {
    flex: 1,
    backgroundColor: '#fff',
  },
  listItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 14,
    paddingHorizontal: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0',
  },
  listItemText: {
    fontSize: 14,
    color: '#333',
    flex: 1,
    fontWeight: 'bold',
  },
  listItemTextBold: { fontWeight: 'bold' },  listItemTextBoldRed: { fontWeight: 'bold', color: '#e30613' },  listItemTextRed: {
    color: '#e30613',
    fontWeight: 'bold',
  },
  arrowRight: {
    fontSize: 20,
    color: '#999',
    marginLeft: 10,
  },
  arrowDown: {
    transform: [{ rotate: '90deg' }],
    color: '#e30613',
  },
  // SubcategorÃ­as
  subcategoriesContainer: {
    backgroundColor: '#fafafa',
    paddingLeft: 20,
  },
  subcategoryItem: {
    paddingVertical: 10,
    paddingHorizontal: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#eee',
  },
  subcategoryText: {
    fontSize: 13,
    color: '#555',
  },
  subcategoryTextVerTodos: {
    fontSize: 13,
    color: '#e30613',
    fontWeight: 'bold',
  },
});

export default CategoriasMobil;


